/* eslint-disable no-prototype-builtins */
import Icon, { InfoCircleFilled, SettingOutlined } from '@ant-design/icons';
import { Avatar, Button, Col, Grid, Menu, Row, Select, Tooltip, Typography } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clearSetCurrentClass, getCurrentClass, getListClass, getUserProfile } from '@store/actions';
import {
  ICInformation,
  SVGBook,
  SVGForum,
  SVGForumGuru,
  SVGForumKelas,
  SVGForumSekolah,
  SVGJurnal,
  SVGKelasku,
  SVGLpa,
  SVGRekapNilai,
  SVGSettings,
  SVGUjian,
} from '../../../assets';
import SVGPembayaran from '../../../assets/icon/svg/money-stack-o.svg';
import SVGRaporParent from '../../../assets/icon/svg/paper-white.svg';
import SVGRaporKuanti from '../../../assets/icon/svg/paper-pulse-white.svg';
import SVGRaporKuali from '../../../assets/icon/svg/paper-edit-white.svg';
import SVGDashboard from '../../../assets/icon/svg/new-dashboard.svg';
import SVGJadwal from '../../../assets/icon/svg/new-calender-o.svg';
import SVGAgenda from '../../../assets/icon/svg/agenda-o.svg';
import SVGBankSoal from '../../../assets/icon/svg/file-shield-alt.svg';
import SVGKegiatanBelajar from '../../../assets/icon/svg/monitor-o.svg';
import SVGBimbingan from '../../../assets/icon/svg/chat-alt-2-o.svg';
import SVGAbsensi from '../../../assets/icon/svg/file-check-white-alt.svg';
import SVGTugas from '../../../assets/icon/svg/file-edit-alt.svg';
import SVGPBLIcon from '../../../assets/icon/svg/pbl-icon.svg';
import SVGPodium from '../../../assets/icon/svg/podium.svg';
import defaultSchool from '../../../assets/icon/svg/school_default.svg';
import './menus.scss';
import { getSchoolById, switchClass, getSchoolFeatures } from '../../../redux/actions';
import IconCreate from '../../atoms/IconCreate';
import { getEperpusDisplayNameFromSchool } from '../../../views/MainApp/EPerpusV1/services/bucket';
import { loadCustomMenusFromWhitelabelAsync, mergeMenus } from './customMenuIntegration';
import { mapFeaturesToMenuItems } from './featureMenuIntegration';
import i18n from '../../../i18n-init';

const buildIconMapFromMenus = (menus, acc = {}) => {
  if (!Array.isArray(menus)) return acc;
  menus.forEach((menu) => {
    const key = menu && (menu.path || menu.id);
    if (menu && key && menu.icon) {
      acc[key] = menu.icon;
    }
    if (menu && Array.isArray(menu.children) && menu.children.length > 0) {
      buildIconMapFromMenus(menu.children, acc);
    }
  });
  return acc;
};

const { SubMenu, Item } = Menu;
const isElipsis = (str) => str?.length > 16;
const renderTooltipWhenElipsis = (str, disabled = false) => {
  if (isElipsis(str) && !disabled) {
    return (
      <Tooltip trigger="hover" title={str}>
        {str}
      </Tooltip>
    );
  }
  return str;
};

const DefaultIconSchool = () => <img src={defaultSchool} alt="icon" />;
const CDefaultIconSchool = () => <Icon component={DefaultIconSchool} height={50} width={50} />;
const renderMenu = (menu, setSelectedKey, history, siteType, isMobile = false) => {
  const click = (e) => {
    // Check if it's an external link
    const isExternal =
      e.external || e.path?.startsWith('http://') || e.path?.startsWith('https://') || e.path?.startsWith('mailto:');

    if (isExternal) {
      // Open external link
      const openInNewTab = e.openInNewTab !== false; // Default true for external links
      if (openInNewTab) {
        window.open(e.path, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = e.path;
      }
    } else {
      // Internal navigation
      setSelectedKey(`/${e.path}`);
      history.push(`/${e.path}`, {
        state: e?.state ?? null,
      });
    }
  };
  const sortIt = (childs) => {
    if (siteType === 'wakaf') {
      return childs.sort((a, b) => {
        if (a?.sequence || b?.sequence) {
          return (a?.sequence ?? 0) - (b?.sequence ?? 0);
        }
        return 0;
      });
    }
    return childs;
  };

  if (menu?.children) {
    if (menu.children.length > 1) {
      return (
        <SubMenu
          key={menu.id}
          title={renderTooltipWhenElipsis(menu?.name ?? '', isMobile)}
          icon={<IconCreate svg={menu.icon} />}
        >
          {sortIt(menu.children).map((child) => (
            <Item onClick={() => click(child)} key={`/${child.path}`} icon={<IconCreate svg={child.icon} />}>
              {renderTooltipWhenElipsis(child?.name ?? '', isMobile)}
            </Item>
          ))}
        </SubMenu>
      );
    }
    const single = menu.children[0];
    if (single) {
      return (
        <Item
          onClick={() => click(single)}
          key={`/${single?.path}`}
          icon={<IconCreate svg={single?.icon ?? SVGDashboard} />}
        >
          {renderTooltipWhenElipsis(single?.name ?? '', isMobile)}
        </Item>
      );
    }
  }
  if (menu.path === null) {
    return <></>;
  }
  return (
    <Item onClick={() => click(menu)} key={`/${menu.path}`} icon={<IconCreate svg={menu.icon} />}>
      {renderTooltipWhenElipsis(menu?.name ?? '', isMobile)}
    </Item>
  );
};

function Menus(props) {
  const stateGlobal = useSelector((state) => state);
  const whitelabelRoot = useSelector((state) => state.General.whitelabel);
  const whitelabelDictionary = useSelector((state) => state.General.dictionaries);
  const findWlData = (key) => {
    if (whitelabelRoot?.length > 0) {
      const findSitename = whitelabelRoot.filter((e) => e.key === key);
      if (findSitename.length > 0) {
        return findSitename[0].value;
      }
    }
    return null;
  };
  const { xs } = Grid.useBreakpoint();
  const siteType = findWlData('site_type');
  const curClass = useSelector((state) => state.Role.value);
  const bodyWeight = useSelector((state) => state.Pengaturan.refBody);
  const whiteLabelData = useSelector((state) => state.General.whitelabel_data);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [selectedKey, setSelectedKey] = useState(`${window.location.pathname}`);
  const [disallow_production, setDisallowProduction] = useState(process.env.REACT_APP_DISABLED_MENUS?.split(',') ?? []);
  const [state, setState] = useState({
    dataClass: [],
    currentClass: false,
    deleteModal: false,
    dataDelete: false,
    isSubmit: false,
  });
  const [myClass, setMyClass] = useState({});
  const [detailSchool, setDetailSchool] = useState({});
  const [customMenus, setCustomMenus] = useState([]);
  const history = useHistory();
  const featureConfig = useSelector((globalState) => globalState.General.features);
  let featureList = null;
  if (featureConfig === null) {
    featureList = null;
  } else if (!featureConfig) {
    featureList = [];
  } else if (Array.isArray(featureConfig)) {
    featureList = featureConfig;
  } else if (Array.isArray(featureConfig.features)) {
    featureList = featureConfig.features;
  } else if (Array.isArray(featureConfig.data?.features)) {
    featureList = featureConfig.data.features;
  } else {
    featureList = [];
  }

  const resolveMenuLabel = (feature) => {
    const rawCode = feature.code || feature.path || feature.id || '';
    const code = rawCode.startsWith('#') ? rawCode.slice(1) : rawCode;
    if (!code) {
      return feature.name || '';
    }

    // Translation keys in this codebase historically use `<code>.menu` (e.g. `lpa.menu`, `dashboard.menu`).
    // Some integrations may also use `menu.<code>`, so we keep that as a fallback for LOCAL i18n lookups.
    //
    // Priority requirement:
    // - If whitelabel dictionary provides `<code>.menu`, use it (highest priority)
    // - Else prefer feature name from school-features API
    // - Else fall back to local i18n JSON resources
    const menuKey = `${code}.menu`;
    const translationKeyCandidates = [menuKey, `menu.${code}`];
    const pickTranslated = (key, fixedT) => {
      const translated = fixedT ? fixedT(key) : t(key);
      return translated && translated !== key ? translated : null;
    };

    // `eperpus-v1` label is driven by the school's own `feature_config.naika` flag
    // (BNS-3098), not by the master-menu name coming from the feature API.
    // Keep this ahead of every other source so a Naika school shows
    // "Repositori Materi" whether its sidebar is built from `baseMenus` or from
    // the school-features tree, and stays consistent with the EPerpus breadcrumb.
    if (code === 'eperpus-v1') {
      return getEperpusDisplayNameFromSchool(stateGlobal?.School?.schoolDetail, curClass?.school_id);
    }

    if (whitelabelDictionary) {
      // Only treat `<code>.menu` as an authoritative dictionary key.
      // If the whitelabel JSON doesn't follow this format, we intentionally ignore it
      // and let the feature API name win.
      if (Object.prototype.hasOwnProperty.call(whitelabelDictionary, menuKey)) {
        const translated = pickTranslated(menuKey);
        if (translated) return translated;
      }
    }

    if (feature.name) {
      return feature.name;
    }

    if (siteType) {
      const fixedT = i18n.getFixedT(siteType);
      for (const key of translationKeyCandidates) {
        const localized = pickTranslated(key, fixedT);
        if (localized) return localized;
      }
    }

    for (const key of translationKeyCandidates) {
      const fallback = pickTranslated(key);
      if (fallback) return fallback;
    }

    return feature.name || translationKeyCandidates[0];
  };
  const dataTop = [
    {
      id: 'dashboard',
      name: t('dashboard.menu'),
      icon: SVGDashboard,
      path: 'dashboard',
      access: ['SISWA', 'GURU', 'WALIKELAS', 'ORTU'],
    },
    {
      id: 'kelasku-revamp-siswa',
      name: t('kelasku.menu'),
      icon: SVGKelasku,
      path: 'revamp/class/view',
      state: myClass,
      access: ['SISWA'],
    },
    {
      id: 'kelasku-revamp-guru',
      name: t('kelasku.menu'),
      icon: SVGKelasku,
      path: 'revamp/class/view',
      access: ['GURU', 'WALIKELAS', 'ORTU'],
    },
  ];
  // const psbSekolah = [
  //   {
  //     id: 'psb',
  //     name: 'PSB',
  //     icon: SVGPsb,
  //     path: null,
  //     access: ['WALIKELAS', 'ADMIN'],
  //     children: [
  //       {
  //         id: 'master-tahapan',
  //         name: 'Data Tahapan',
  //         icon: SVGPsb,
  //         path: 'psb/master-tahapan',
  //         access: ['WALIKELAS', 'ADMIN'],
  //       },
  //       {
  //         id: 'master-gelombang',
  //         name: 'Gelombang Pendaftaran',
  //         icon: SVGPsb,
  //         path: 'psb/gelombang',
  //         access: ['WALIKELAS', 'ADMIN'],
  //       },
  //     ],
  //   },
  // ];
  const kelasDigital = [
    {
      id: 'kelas-digital-main',
      name: t('rcb.menu'),
      icon: SVGRaporParent,
      path: null,
      access: ['GURU', 'WALIKELAS', 'ORTU', 'SISWA'],
      children: [
        {
          id: 'agenda',
          name: 'Pengumuman',
          icon: SVGAgenda,
          path: 'agenda',
          access: ['GURU', 'WALIKELAS', 'ORTU', 'SISWA'],
        },
        {
          id: 'revamp-jadwal',
          name: t('agenda.menu'),
          icon: SVGJadwal,
          path: 'revamp/jadwal',
          access: ['GURU', 'WALIKELAS', 'SISWA', 'ORTU'],
        },
        {
          id: 'lembar-materi',
          name: t('materi.LABEL_NAME'),
          icon: SVGBook,
          sequence: 1,
          path: 'lembar-materi',
          access: ['GURU', 'WALIKELAS', 'SISWA', 'ORTU'],
        },
        {
          id: 'bank-soal',
          name: t('bank_soal.menu'),
          icon: SVGBankSoal,
          path: 'bank-soal',
          access: ['GURU', 'WALIKELAS'],
        },
      ],
    },
  ];
  const laporanSiswa = [
    {
      id: 'laporan-main',
      name: t('kgb.menu'),
      icon: SVGKegiatanBelajar,
      path: null,
      access: ['GURU', 'WALIKELAS', 'ORTU', 'SISWA'],
      children: [
        {
          id: 'revamp-absensi',
          name: t('absensi.menu'),
          icon: SVGAbsensi,
          path: 'revamp/absensi',
          access: ['GURU', 'WALIKELAS', 'SISWA', 'ORTU'],
        },
        {
          id: 'tugas-revamp',
          sequence: 2,
          name: t('tugas.menu'),
          icon: SVGTugas,
          path: 'revamp/tugas',
          access: ['GURU', 'WALIKELAS', 'ORTU', 'SISWA'],
        },
        {
          id: 'ujian-guru',
          name: t('ujian.menu'),
          icon: SVGAbsensi,
          path: 'quiz',
          access: ['GURU', 'WALIKELAS'],
        },
        {
          id: 'ujian-siswa',
          name: t('ujian.menu'),
          icon: SVGUjian,
          path: 'ujian-siswa',
          access: ['SISWA', 'ORTU'],
        },
        {
          id: 'dokumen',
          name: t('dokumen.menu'),
          icon: SVGRaporParent,
          path: 'dokumen',
          access: ['GURU', 'WALIKELAS', 'SISWA', 'ORTU'],
        },
        {
          id: 'eperpus',
          // name: t('eperpus.menu'),
          name: 'Buku Referensi',
          icon: SVGKelasku,
          path: 'eperpus',
          access: ['GURU', 'WALIKELAS', 'ORTU', 'SISWA'],
        },
        {
          id: 'eperpus-v1',
          name: getEperpusDisplayNameFromSchool(stateGlobal?.School?.schoolDetail, curClass?.school_id),
          icon: SVGKelasku,
          path: 'eperpus-v1',
          access: ['GURU', 'WALIKELAS', 'ORTU', 'SISWA'],
        },
        {
          id: 'revamp-konferensi',
          name: 'Kelas Online',
          icon: SVGPodium,
          path: 'revamp/conference',
          access: ['GURU', 'WALIKELAS', 'SISWA', 'ORTU'],
        },
        {
          id: 'jurnal-belajar',
          name: t('jurnal.LABEL_NAME'),
          icon: SVGJurnal,
          sequence: 1,
          path: 'jurnal-belajar',
          access: ['GURU', 'WALIKELAS', 'SISWA', 'ORTU'],
        },
        {
          id: 'blended-learning',
          name: 'Penjadwalan',
          icon: SVGKelasku,
          path: 'blended-learning',
          access: ['WALIKELAS', 'ADMIN'],
        },
        {
          id: 'blended-learning',
          name: 'Penjadwalan',
          icon: SVGKelasku,
          path: 'blended-learning/detail',
          access: ['ORTU'],
        },
        {
          id: 'laporan-nilai-siswa',
          name: `Laporan Nilai ${t('role.SISWA')}`,
          icon: SVGUjian,
          path: 'laporan-nilai-siswa',
          access: ['GURU', 'WALIKELAS'],
        },
        {
          id: 'pbl',
          name: 'PBL',
          icon: SVGPBLIcon,
          path: 'pbl',
          access: ['GURU', 'WALIKELAS', 'ORTU', 'SISWA'],
        },
      ],
    },
  ];
  const penilaianMain = [
    {
      id: 'penialian-main',
      name: 'Penilaian',
      icon: SVGRaporParent,
      path: null,
      access: ['WALIKELAS', 'ADMIN', 'ORTU', 'SISWA', 'GURU'],
      children: [
        {
          id: 'lpa',
          onlyTK: false,
          name: t('lpa.menu'),
          icon: SVGLpa,
          path: 'lpa',
          access: ['WALIKELAS', 'GURU'],
        },
        {
          id: 'lpa-report',
          onlyTK: false,
          name: t('lpa.menu'),
          icon: SVGUjian,
          path: 'lpa-report',
          access: ['ORTU', 'SISWA'],
        },
        {
          id: 'rekap-nilai-refactor',
          name: t('rekap_nilai.menu'),
          icon: SVGRekapNilai,
          access: ['GURU', 'WALIKELAS'],
          path: 'score-refactor',
        },
        {
          id: 'rapor-kuanti',
          name: 'Rapor Kuantitatif',
          icon: SVGRaporKuanti,
          path: 'rapor',
          access: ['WALIKELAS', 'ADMIN', 'ORTU', 'SISWA', 'GURU'],
        },
        {
          id: 'rapor-kuali',
          name: 'Rapor Kualitatif',
          icon: SVGRaporKuali,
          path: 'rapor-qualitative',
          access: ['WALIKELAS', 'ADMIN', 'ORTU', 'SISWA', 'GURU'],
        },
        {
          id: 'bimbingan-konseling',
          name: 'Bimbingan Konseling',
          icon: SVGBimbingan,
          path: 'riwayat-poin',
          access: ['ORTU'],
          onlyTK: false,
        },
        {
          id: 'bimbingan-konseling-siswa',
          name: 'Bimbingan Konseling',
          icon: SVGBimbingan,
          path: 'riwayat-poin',
          onlyTK: true,
          access: ['SISWA'],
        },
      ],
    },
  ];
  const pembayaranMain = [
    {
      id: 'tagihan-ortu',
      name: 'Pembayaran',
      icon: SVGPembayaran,
      path: 'tagihan-ortu',
      access: ['ORTU'],
    },
    {
      id: 'tagihan',
      name: 'Pembayaran',
      icon: SVGPembayaran,
      path: 'tagihan',
      access: ['ADMIN'],
    },
  ];
  const forumMain = [
    {
      id: 'forum',
      name: 'Forum',
      icon: SVGForum,
      path: null,
      access: ['WALIKELAS', 'ADMIN', 'ORTU', 'SISWA', 'GURU'],
      children: [
        {
          id: 'forum-sekolah',
          name: 'Forum Sekolah',
          icon: SVGForumSekolah,
          path: 'forum-sekolah',
          access: ['WALIKELAS', 'ADMIN', 'ORTU', 'SISWA', 'GURU'],
        },
        {
          id: 'forum-kelas',
          name: 'Forum Kelas',
          icon: SVGForumKelas,
          path: 'forum-kelas',
          access: ['WALIKELAS', 'ADMIN', 'ORTU', 'SISWA', 'GURU'],
        },
        {
          id: 'forum-guru',
          name: 'Forum Guru',
          icon: SVGForumGuru,
          path: 'forum-guru',
          access: ['WALIKELAS', 'ADMIN', 'GURU'],
        },
      ],
    },
  ];
  const settingMain = [
    {
      id: 'pengaturan',
      name: 'Pengaturan',
      icon: SVGSettings,
      path: 'pengaturan',
      access: ['ADMIN'],
    },
  ];

  const information = [
    {
      id: 'informasi',
      name: 'Informasi Proses Sistem',
      icon: ICInformation,
      path: 'information',
      access: ['ADMIN', 'GURU', 'WALIKELAS'],
    },
  ];

  // Base menus (original)
  const baseMenus = [
    ...dataTop
      .concat(kelasDigital)
      .concat(laporanSiswa)
      .concat(penilaianMain)
      .concat(pembayaranMain)
      .concat(forumMain)
      .concat(information)
      .concat(settingMain),
  ];

  const iconMapByCode = useMemo(() => buildIconMapFromMenus(baseMenus), [baseMenus]);

  const featureMenus = useMemo(() => {
    if (!Array.isArray(featureList) || featureList.length === 0) {
      return [];
    }
    return mapFeaturesToMenuItems(featureList, { resolveLabel: resolveMenuLabel, iconMap: iconMapByCode });
  }, [featureList, resolveMenuLabel, iconMapByCode]);

  // Priority:
  // - Always have a usable menu from the base (local JSON / hardcoded structure)
  // - Only replace it with feature-driven menus when the school-feature endpoint returns
  //   a non-empty (selected) feature tree
  const effectiveBaseMenus = featureMenus && featureMenus.length > 0 ? featureMenus : baseMenus;

  // Merge with custom menus from whitelabel (placed at the bottom/end)
  const menus = mergeMenus(effectiveBaseMenus, customMenus, 'bottom');
  const renderStyles = () => {
    if (!xs) {
      return {
        height: bodyWeight,
      };
    }
    return undefined;
  };
  const checkRole = (e) => {
    if (disallow_production.includes(e.id)) {
      return false;
    }
    if (e.onlyTK && stateGlobal?.School?.schoolDetail?.grade && stateGlobal?.School?.schoolDetail?.grade !== 'TK') {
      return false;
    }
    if (e?.children) {
      e.children = e.children.filter((child) => checkRole(child));
    }
    if (e?.access) {
      if (e.id === 'tagihan' && curClass?.is_payment_admin) {
        return true;
      }
      if (e.access.includes('ADMIN') && curClass?.is_admin) {
        return true;
      }
      return e.access.includes(curClass.class_role);
    }
    return true;
  };
  useEffect(() => {
    if (stateGlobal.Role.value.class_role !== '') {
      dispatch(getCurrentClass());
      dispatch(getUserProfile());
      dispatch(getListClass(stateGlobal.Role.value));
    }
  }, [stateGlobal.Role.value]);
  useEffect(() => {
    if (stateGlobal.Class.listClass) {
      setMyClass(stateGlobal.Class.listClass.length > 0 ? stateGlobal.Class.listClass[0] : null);
      const data = stateGlobal.Class.listClass.length > 0 ? stateGlobal.Class.listClass : [];
      if (data) {
        setState({ ...state, dataClass: data });
      }
    }
  }, [stateGlobal.Class.listClass]);

  useEffect(() => {
    if (stateGlobal.Class.currentClass) {
      const data = stateGlobal.Class.currentClass ? stateGlobal.Class.currentClass : false;
      if (data) {
        setState({ ...state, currentClass: data });
        dispatch(clearSetCurrentClass());
      }
    }
  }, [stateGlobal.Class.currentClass]);

  useEffect(() => {
    if (stateGlobal.Class.setCurrentClass) {
      dispatch(getCurrentClass());
      dispatch(getListClass(stateGlobal.Role.value));
    }
  }, [stateGlobal.Class.setCurrentClass]);

  useEffect(() => {
    if (stateGlobal.Class.deleteClass) {
      setState({
        ...state,
        dataDelete: false,
        deleteModal: false,
        isSubmit: false,
      });
      dispatch(getCurrentClass());
      dispatch(getListClass(stateGlobal.Role.value));
    }
  }, [stateGlobal.Class.deleteClass]);

  useEffect(() => {
    if (curClass?.school_id) {
      dispatch(getSchoolById(curClass?.school_id)).then((e) => {
        setDetailSchool(e);
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    if (curClass?.school_id) {
      dispatch(getSchoolFeatures(curClass.school_id));
    }
  }, [curClass?.school_id]);

  useEffect(() => {
    if (whiteLabelData.hidden_menus && whiteLabelData.hidden_menus.length > 0) {
      setDisallowProduction(disallow_production.concat(whiteLabelData.hidden_menus));
    }
  }, [whiteLabelData.hidden_menus]);

  // Load custom menus from whitelabel (supports both direct JSON and file URL)
  useEffect(() => {
    let isMounted = true;

    const loadCustomMenus = async () => {
      if (whitelabelRoot && Array.isArray(whitelabelRoot)) {
        const loadedCustomMenus = await loadCustomMenusFromWhitelabelAsync(whitelabelRoot);

        // Only update state if component is still mounted
        if (isMounted) {
          setCustomMenus(loadedCustomMenus);
          if (loadedCustomMenus.length > 0) {
            console.log('Custom menus loaded from whitelabel:', loadedCustomMenus);
          }
        }
      }
    };

    loadCustomMenus();

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, [whitelabelRoot]);

  return (
    <div className="main-menu">
      <div className="main-menu-info">
        <Row gutter={10}>
          <Col span={6}>
            {detailSchool && detailSchool?.logo_image_url !== '' && (
              <Avatar src={detailSchool?.logo_image_url} size={40} />
            )}
            {detailSchool && detailSchool?.logo_image_url === '' && (
              <Avatar
                icon={<CDefaultIconSchool />}
                size={40}
                style={{
                  color: 'var(--primary-color)',
                  background: 'transparent',
                }}
              />
            )}
            {!detailSchool && (
              <Avatar
                icon={<CDefaultIconSchool />}
                size={40}
                style={{
                  color: 'var(--primary-color)',
                  background: 'transparent',
                }}
              />
            )}
          </Col>
          <Col span={18}>
            {stateGlobal.Role.value.class_role !== '' && (
              <>
                <div
                  className="main-menu-info-role"
                  onClick={() => {
                    history.push('/ganti-sekolah');
                  }}
                >
                  <Typography.Text>{state.currentClass.school_name}</Typography.Text>
                </div>
              </>
            )}
          </Col>
        </Row>
        <Row style={{ marginTop: 10 }}>
          <Col span={20}>
            {stateGlobal.Role.value.class_role === '' ? (
              <Button
                block
                type="primary"
                icon={<SettingOutlined />}
                onClick={() => {
                  history.push('/gabung-sekolah');
                }}
              >
                Gabung Sekolah
              </Button>
            ) : (
              <>
                <Select
                  className="dropdown-primary"
                  dropdownClassName="dropdown-primary-active"
                  placeholder={
                    state.dataClass.filter((e) => e.class_id === state.currentClass.class_id).length > 0
                      ? 'Pilih Kelas'
                      : 'Kelas Tidak Ada'
                  }
                  style={{
                    width: '100%',
                    borderRadius: '14px !important',
                  }}
                  disabled={state.dataClass.length === 0}
                  value={
                    state.dataClass.filter((e) => e.id === state.currentClass.class_id).length > 0
                      ? state.currentClass.class_id
                      : null
                  }
                  onChange={(r) => {
                    if (r) {
                      dispatch(
                        switchClass({
                          id: r,
                        })
                      );
                    } else {
                      history.push('/pengaturan/kelas', {
                        openModal: true,
                      });
                    }
                  }}
                >
                  {state.dataClass.map((r) => (
                    <Select.Option key={r.id} value={r.id}>
                      {r.name}
                    </Select.Option>
                  ))}
                  {((curClass.class_role === 'WALIKELAS' && curClass.is_admin) || curClass.is_admin) && (
                    <Select.Option
                      key="new"
                      value={0}
                      style={{
                        color: 'var(--primary-color)',
                        cursor: 'pointer',
                        textAlign: 'center',
                        pointerEvents: 'all',
                      }}
                    >
                      Tambah Kelas Baru
                    </Select.Option>
                  )}
                </Select>
              </>
            )}
          </Col>
          <Col span={4}>
            {stateGlobal.Role.value.class_role !== '' && (
              <Tooltip title="Detail Kelas" trigger="hover">
                <Button
                  shape="circle"
                  type="link"
                  style={{ marginLeft: 5 }}
                  icon={<InfoCircleFilled />}
                  onClick={() => {
                    history.push('/revamp/class/view');
                  }}
                />
              </Tooltip>
            )}
          </Col>
        </Row>
      </div>
      <div className="main-menu-box" style={renderStyles()}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div>
            <Menu
              onClick={props.onSelect}
              className="sidebar-menu "
              selectedKeys={[selectedKey]}
              defaultOpenKeys={['main-0']}
              mode="inline"
              style={{ paddingBottom: 0 }}
            >
              {menus.filter((e) => checkRole(e)).map((menu) => renderMenu(menu, setSelectedKey, history, siteType, xs))}
            </Menu>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Menus;
